'use client';

/**
 * Floating Chat Context
 *
 * Manages the state of the floating AI chatbot panel.
 * The floating button opens the AI assistant directly (no conversation list).
 * Regular conversations are handled by the inbox.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import * as Sentry from '@sentry/nextjs';

const FLOATING_CHAT_COOKIE_NAME = 'floating-chat:state';
const FLOATING_CHAT_COOKIE_MAX_AGE = 60 * 60 * 24 * 30; // 30 days
const FLOATING_CHAT_DIMENSIONS_KEY = 'floating-chat:dimensions';
const FLOATING_CHAT_CONVERSATION_KEY = 'floating-chat:conversation';

const DEFAULT_DIMENSIONS = { width: 380, height: 520 };
const MIN_DIMENSIONS = { width: 320, height: 400 };
const MAX_DIMENSIONS = { width: 500, height: 700 };

interface FloatingChatDimensions {
  width: number;
  height: number;
}

/** Header context for an item-anchored override conversation. */
interface FloatingChatOverrideDetails {
  /** Conversation title (e.g. the seller's display name). */
  title?: string;
  /** Secondary line under the title (e.g. the listing the DM is about). */
  subtitle?: string;
  /** Avatar shown next to the title (e.g. the seller's profile photo). */
  avatarUrl?: string | null;
}

interface FloatingChatContextValue {
  // Panel open/collapsed state
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;

  // Selected conversation (the Ballee AI conversation, persisted)
  selectedConversationId: string | null;
  setSelectedConversationId: (id: string | null) => void;

  // Item-anchored override (e.g. the marketplace "Message seller" button).
  // Transient — NOT persisted — so the floating button keeps reopening the AI
  // assistant. When set, the panel shows THIS conversation instead of the AI
  // one; closing the panel clears it (minimizing keeps it).
  overrideConversationId: string | null;
  overrideTitle: string | null;
  overrideSubtitle: string | null;
  overrideAvatarUrl: string | null;
  openConversation: (
    conversationId: string,
    details?: FloatingChatOverrideDetails,
  ) => void;
  clearOverride: () => void;

  // Panel dimensions
  dimensions: FloatingChatDimensions;
  setDimensions: (dims: FloatingChatDimensions) => void;

  // Dimension constraints
  minDimensions: FloatingChatDimensions;
  maxDimensions: FloatingChatDimensions;
}

const FloatingChatContext = createContext<FloatingChatContextValue | null>(
  null,
);

interface FloatingChatProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
}

export function FloatingChatProvider({
  children,
  defaultOpen = false,
}: FloatingChatProviderProps) {
  const [isOpen, setIsOpenState] = useState(defaultOpen);
  const [selectedConversationId, setSelectedConversationIdState] = useState<
    string | null
  >(null);
  const [dimensions, setDimensionsState] =
    useState<FloatingChatDimensions>(DEFAULT_DIMENSIONS);
  const [overrideConversationId, setOverrideConversationId] = useState<
    string | null
  >(null);
  const [overrideTitle, setOverrideTitle] = useState<string | null>(null);
  const [overrideSubtitle, setOverrideSubtitle] = useState<string | null>(null);
  const [overrideAvatarUrl, setOverrideAvatarUrl] = useState<string | null>(
    null,
  );

  // Load persisted state on mount (client-side only)
  useEffect(() => {
    // Load open state from cookie
    const cookies = document.cookie.split(';');
    const stateCookie = cookies.find((c) =>
      c.trim().startsWith(`${FLOATING_CHAT_COOKIE_NAME}=`),
    );
    if (stateCookie) {
      const value = stateCookie.split('=')[1]?.trim();
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync from cookie on mount
      setIsOpenState(value === 'true');
    }

    // Load dimensions from localStorage
    try {
      const stored = localStorage.getItem(FLOATING_CHAT_DIMENSIONS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as FloatingChatDimensions;
        const validDimensions = {
          width: Math.max(
            MIN_DIMENSIONS.width,
            Math.min(MAX_DIMENSIONS.width, parsed.width),
          ),
          height: Math.max(
            MIN_DIMENSIONS.height,
            Math.min(MAX_DIMENSIONS.height, parsed.height),
          ),
        };
        setDimensionsState(validDimensions);
      }
    } catch {
      // Ignore localStorage errors
    }

    // Load conversation ID from localStorage
    try {
      const storedConversationId = localStorage.getItem(
        FLOATING_CHAT_CONVERSATION_KEY,
      );
      if (storedConversationId) {
        setSelectedConversationIdState(storedConversationId);
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Persist open state to cookie
  const setIsOpen = useCallback((open: boolean) => {
    setIsOpenState(open);
    document.cookie = `${FLOATING_CHAT_COOKIE_NAME}=${open}; path=/; max-age=${FLOATING_CHAT_COOKIE_MAX_AGE}`;
  }, []);

  const toggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  // Persist conversation ID to localStorage
  const setSelectedConversationId = useCallback((id: string | null) => {
    setSelectedConversationIdState(id);
    try {
      if (id) {
        localStorage.setItem(FLOATING_CHAT_CONVERSATION_KEY, id);
      } else {
        localStorage.removeItem(FLOATING_CHAT_CONVERSATION_KEY);
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  // Open the panel pointed at a specific conversation (item-anchored). The
  // override is transient state only — never written to localStorage — so the
  // AI assistant remains what the floating button reopens.
  const openConversation = useCallback(
    (conversationId: string, details?: FloatingChatOverrideDetails) => {
      setOverrideConversationId(conversationId);
      setOverrideTitle(details?.title ?? null);
      setOverrideSubtitle(details?.subtitle ?? null);
      setOverrideAvatarUrl(details?.avatarUrl ?? null);
      setIsOpen(true);
    },
    [setIsOpen],
  );

  const clearOverride = useCallback(() => {
    setOverrideConversationId(null);
    setOverrideTitle(null);
    setOverrideSubtitle(null);
    setOverrideAvatarUrl(null);
  }, []);

  // Persist dimensions to localStorage
  const setDimensions = useCallback((dims: FloatingChatDimensions) => {
    const validDimensions = {
      width: Math.max(
        MIN_DIMENSIONS.width,
        Math.min(MAX_DIMENSIONS.width, dims.width),
      ),
      height: Math.max(
        MIN_DIMENSIONS.height,
        Math.min(MAX_DIMENSIONS.height, dims.height),
      ),
    };
    setDimensionsState(validDimensions);

    try {
      localStorage.setItem(
        FLOATING_CHAT_DIMENSIONS_KEY,
        JSON.stringify(validDimensions),
      );
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      toggle,
      selectedConversationId,
      setSelectedConversationId,
      overrideConversationId,
      overrideTitle,
      overrideSubtitle,
      overrideAvatarUrl,
      openConversation,
      clearOverride,
      dimensions,
      setDimensions,
      minDimensions: MIN_DIMENSIONS,
      maxDimensions: MAX_DIMENSIONS,
    }),
    [
      isOpen,
      setIsOpen,
      toggle,
      selectedConversationId,
      setSelectedConversationId,
      overrideConversationId,
      overrideTitle,
      overrideSubtitle,
      overrideAvatarUrl,
      openConversation,
      clearOverride,
      dimensions,
      setDimensions,
    ],
  );

  return (
    <FloatingChatContext.Provider value={value}>
      {children}
    </FloatingChatContext.Provider>
  );
}

/**
 * `openConversation` is the one method on the no-provider fallback whose
 * silent no-op is a genuine bug, not a benign default: a caller (e.g. the
 * marketplace "Message seller" button) that reaches it has already created a
 * conversation server-side, and the panel will never open with no provider in
 * the tree — the exact "nothing happens" class behind BAL-322. Keep the
 * no-throw contract (SSR + other consumers must not break) but make the dead
 * path observable: console.error in dev, a Sentry message in production.
 */
function reportMissingProvider() {
  const message =
    'useFloatingChat().openConversation called with no FloatingChatProvider in tree';
  if (process.env.NODE_ENV !== 'production') {
    console.error(message);
    return;
  }
  Sentry.captureMessage(message, { level: 'error' });
}

const defaultContextValue: FloatingChatContextValue = {
  isOpen: false,
  setIsOpen: () => {},
  toggle: () => {},
  selectedConversationId: null,
  setSelectedConversationId: () => {},
  overrideConversationId: null,
  overrideTitle: null,
  overrideSubtitle: null,
  overrideAvatarUrl: null,
  openConversation: () => reportMissingProvider(),
  clearOverride: () => {},
  dimensions: DEFAULT_DIMENSIONS,
  setDimensions: () => {},
  minDimensions: MIN_DIMENSIONS,
  maxDimensions: MAX_DIMENSIONS,
};

export function useFloatingChat() {
  const context = useContext(FloatingChatContext);

  if (!context) {
    return defaultContextValue;
  }

  return context;
}

export { FloatingChatContext };
